import React from 'react';
import { Ban, Eye, Users } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { deactivatePhysicianGroupOptions, reactivatePhysicianGroupOptions } from '@generated';
import { createQueryDataTable, type PropsOfQueryDataTable, type RowOfQueryDataTable } from '~/shared/data-tables/query/QueryDataTable';
import { Prompt } from '~/shared/components/Prompt';
import { ActiveColumnCell } from '~/shared/data-tables/column-types/ActiveColumn';
import { useMutation } from '~/util/http/useMutation';
import { getSearchQueryKey } from '~/util/http/useSearch';
import { Button } from '~/shared/components/buttons/Button';
import { TableActions } from '~/shared/data-tables/TableActions';

const PhysicianGroupsDataTable = createQueryDataTable('physician-group');
type RowType = RowOfQueryDataTable<typeof PhysicianGroupsDataTable>;
type TableProps = PropsOfQueryDataTable<typeof PhysicianGroupsDataTable>;

function ActionsCell({ row }: { row: RowType }) {
	const navigate = useNavigate();

	const deactivatePhysicianGroup = useMutation({
		...deactivatePhysicianGroupOptions,
		invalidateQueryKeys: [getSearchQueryKey('physician-group')]
	});

	const reactivatePhysicianGroup = useMutation({
		...reactivatePhysicianGroupOptions,
		invalidateQueryKeys: [getSearchQueryKey('physician-group')]
	});

	return (
		<TableActions>
			{row.active ? (
				<>
					<TableActions.ViewButton aria-label="Edit Physician Group" onClick={() => navigate(`edit/${row.id}`)} />
					<Button
						Icon={Ban}
						aria-label="Deactivate Physician Group"
						intent="destructive"
						appearance="ghost"
						renderModal={closeModal => (
							<Prompt
								title="Deactivate Physician Group"
								text="Are you sure you want to deactivate this group?"
								onAction={() => {
									deactivatePhysicianGroup.mutateAsync({ pharmacyId: row.id });
									closeModal();
								}}
								onCancel={closeModal}
							/>
						)}
					/>
				</>
			) : (
				<Button
					Icon={Eye}
					aria-label="Reactivate Physician Group"
					appearance="ghost"
					renderModal={closeModal => (
						<Prompt
							title="Reactivate Physician Group"
							text="Are you sure you want to reactivate this group?"
							onAction={() => {
								reactivatePhysicianGroup.mutateAsync({ pharmacyId: row.id });
								closeModal();
							}}
							onCancel={closeModal}
						/>
					)}
				/>
			)}
		</TableActions>
	);
}

function PhysicianCountCell({ row }: { row: RowType }) {
	return (
		<div className="flex flex-row justify-center gap-1 text-center align-middle">
			<div className="flex items-center gap-2">
				<Users className="size-4 text-gray-400" />
				<span className="text-sm text-gray-900">
					{row.physicianCount} {row.physicianCount === 1 ? 'physician' : 'physicians'}
				</span>
			</div>
		</div>
	);
}

const columnDefinitions = PhysicianGroupsDataTable.columns([
	{
		header: 'Group Name',
		key: 'name',
		sortable: true
	},
	{
		header: 'Physicians',
		key: 'physicianCount',
		sortable: true,
		renderCell: row => <PhysicianCountCell row={row} />
	},
	{
		header: 'Active',
		key: 'active',
		width: 'w-[100px]',
		renderCell: row => <ActiveColumnCell active={row.active} />
	},
	{
		header: 'Actions',
		key: 'actions',
		width: 'w-[100px]',
		renderCell: row => <ActionsCell row={row} />
	}
]);

export function PhysicianGroupTable(props: TableProps) {
	return (
		<PhysicianGroupsDataTable.Table
			columns={columnDefinitions}
			emptyMessage="No physician groups found."
			getMuteRowOpacity={row => !row.active}
			{...props}
		/>
	);
}
